import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// Zwei starke Hook-KPIs direkt unter Hero — fangen den Besucher emotional ein.
const heroKpis = [
  {
    value: 86,
    suffix: '%',
    label: { de: 'ERKENNEN: KI WIRD IHR GESCHÄFT VERÄNDERN.', en: 'RECOGNISE: AI WILL CHANGE THEIR BUSINESS.' },
    caption: {
      de: '86 % der Mittelständler wissen es. Die Frage ist nicht mehr ob. Sondern wer es als Erster umsetzt. Und wer zu lange wartet.',
      en: '86 % of mid-sized businesses know it. The question is no longer if. But who implements it first. And who waits too long.',
    },
    source: 'KI-Studie Mittelstand 2025',
  },
  {
    value: 80,
    suffix: '%',
    prefix: { de: 'BIS', en: 'UP TO' },
    label: { de: 'SCHEITERN TROTZDEM.', en: 'FAIL ANYWAY.' },
    caption: {
      de: 'Fast vier von fünf KI-Projekten erreichen nie messbare Ergebnisse. Fast nie wegen der Technik. Fast immer, weil der Weg ins Tagesgeschäft fehlt.',
      en: 'Almost four out of five AI projects never reach measurable results. Almost never because of the technology. Almost always because the path into daily operations is missing.',
    },
    source: 'VIER / Trend Report 2025',
  },
];

function KpiCard({ kpi, index, lang }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || played) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setPlayed(true);
        const target = kpi.value;
        const duration = 1800;
        const start = performance.now();
        const animate = (now) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 4);
          setCount(Math.round(eased * target));
          if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [kpi.value, played]);

  return (
    <div ref={ref} data-testid={`kpi-card-${index}`}
      className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:border-[#befa0a]/50 transition-all duration-300 hover:bg-white/[0.08]">
      <div className="flex items-baseline gap-2 mb-3">
        {kpi.prefix && (
          <span className="text-white/60 text-xs uppercase tracking-widest font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {kpi.prefix[lang]}
          </span>
        )}
        <span className="text-white text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{count}</span>
        <span className="text-[#befa0a] text-lg font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{kpi.suffix}</span>
      </div>
      <p className="text-white text-sm font-bold uppercase tracking-wide mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
        {kpi.label[lang]}
      </p>
      <p className="text-white/70 text-sm leading-relaxed mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{kpi.caption[lang]}</p>
      <p className="text-white/25 text-[10px] italic" style={{ fontFamily: "'Manrope', sans-serif" }}>{kpi.source}</p>
    </div>
  );
}

export default function HeroSection() {
  const { lang, t } = useLanguage();
  // Sebastian-Fix 2026-06-09: Mobile = nur Bild, Desktop = Bild + Video.
  // Reason: opacity-Fade auf Video verwirrt Lighthouse-LCP-Messung (zählt Video als neues LCP-Element →
  // 32s auf Mobile-4G). Daher: kein opacity-Fade mehr, instant-Swap; auf Mobile gar kein Video.
  const [videoMounted, setVideoMounted] = useState(false);

  useEffect(() => {
    // Video nur auf Desktop laden (>=768px) — Mobile bleibt beim Bild
    if (typeof window === 'undefined') return;
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktop) return;

    const trigger = () => setVideoMounted(true);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(trigger, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(trigger, 2500);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-x-hidden">
      {/* Static dunkler Fallback-Gradient — sichtbar in 0ms falls Bild noch lädt */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a]" />
      {/* Poster-Image — Hero-Background, sofort sichtbar */}
      <img
        src="/assets/hero-poster.webp"
        alt=""
        width="1536"
        height="864"
        fetchPriority="high"
        loading="eager"
        decoding="async"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Video lädt nur auf Desktop, ohne Fade (instant-Swap statt LCP-Verwirrung) */}
      {videoMounted && (
        <video data-testid="hero-video" autoPlay muted loop playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover">
          <source src="/assets/hero-video.mp4" type="video/mp4" />
        </video>
      )}
      <div className="absolute inset-0 bg-black/75" />

      <div className="relative z-10 px-6 max-w-[1400px] mx-auto w-full pt-28 pb-20">
        {/* Desktop: 2-Spalten — links Text/CTA, rechts KPIs. Mobile: alles gestackt. */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-16 items-start">
          {/* Linke Spalte: Badge → Title → Subtitle → Text → (Mobile KPIs) → CTAs */}
          <div className="max-w-2xl">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="hero-badge" className="text-[#befa0a] text-xs md:text-sm uppercase tracking-[0.3em] mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {t('hero.badge')}
            </motion.p>
            <motion.h1 data-testid="hero-title" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
              className="text-white text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[1.1] pt-[0.15em] overflow-visible"
              style={{ fontFamily: "'Oswald', sans-serif", clipPath: 'none' }}>
              {t('hero.title')}
            </motion.h1>
            <motion.p data-testid="hero-subtitle" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              className="text-white/80 text-base md:text-lg mt-6 leading-relaxed font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {t('hero.sub')}
            </motion.p>
            {t('hero.text') && (
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.65 }}
                className="text-white/65 text-sm md:text-base mt-5 leading-relaxed whitespace-pre-line" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t('hero.text')}
              </motion.p>
            )}

            {/* KPIs auf Mobile (unter Text, vor CTAs). Desktop: hidden, dort rechte Spalte. */}
            <motion.div data-testid="hero-kpi-block-mobile" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-10 mb-2 grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
              {heroKpis.map((kpi, i) => (
                <KpiCard key={i} kpi={kpi} index={i} lang={lang} />
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1 }}
              className="mt-10 flex flex-col sm:flex-row items-start gap-4">
              <button data-testid="hero-cta1" onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#befa0a] text-[#0a0a0a] px-10 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors inline-block" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t('hero.cta1')}
              </button>
              <button data-testid="hero-cta2" onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="border border-white/30 text-white px-10 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t('hero.cta2')}
              </button>
            </motion.div>
          </div>

          {/* Rechte Spalte (Desktop only): KPIs vertikal gestapelt */}
          <motion.div data-testid="hero-kpi-block-desktop" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="hidden lg:flex flex-col gap-4 lg:pt-12 w-full">
            {heroKpis.map((kpi, i) => (
              <KpiCard key={i} kpi={kpi} index={i} lang={lang} />
            ))}
          </motion.div>
        </div>
      </div>

      <motion.button data-testid="scroll-indicator" onClick={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-[#befa0a] transition-colors cursor-pointer">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}><ChevronDown size={28} /></motion.div>
      </motion.button>
    </section>
  );
}
